import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash, ShieldCheck, Prohibit, Plus, X } from "@phosphor-icons/react";

const ROLES = ["user", "admin"];
const SUBS = ["trialing", "active", "canceled", "none"];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bl, setBl] = useState([]);
  const [blForm, setBlForm] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, blRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/account-blacklist").catch(() => ({ data: [] })),
      ]);
      setUsers(data);
      setBl(blRes.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addBl = async () => {
    const value = blForm.trim().toLowerCase();
    if (!value) return;
    try {
      const { data } = await api.post("/admin/account-blacklist", { value });
      toast.success(`${value} blacklisté${data.users_disabled ? ` · ${data.users_disabled} compte(s) désactivé(s)` : ""}`);
      setBlForm("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeBl = async (id) => {
    try {
      await api.delete(`/admin/account-blacklist/${id}`);
      toast.success("Retiré de la blacklist");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const patch = async (id, body) => {
    setUsers((u) => u.map((x) => (x.id === id ? { ...x, ...body } : x)));
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success("Utilisateur mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((u) => u.filter((x) => x.id !== id));
      toast.success("Utilisateur supprimé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="overline mb-2">Gestion</div>
      <h1 className="font-display text-3xl font-bold mb-8">Utilisateurs <span className="text-muted text-lg font-mono">({users.length})</span></h1>

      {/* Account blacklist (ESN / users) */}
      <div className="surface rounded-lg p-5 mb-8" data-testid="account-blacklist-card">
        <div className="flex items-center gap-2 mb-1">
          <Prohibit size={18} weight="fill" className="text-red-400" />
          <h2 className="font-display font-bold text-fg">Comptes bloqués (blacklist)</h2>
        </div>
        <p className="text-xs text-muted mb-4">Bloquez une adresse email ou un domaine entier. Les comptes existants sont désactivés et aucune réinscription n'est possible.</p>
        <div className="flex items-center gap-2 mb-4">
          <input value={blForm} onChange={(e) => setBlForm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addBl()}
            placeholder="email@domaine.com ou domaine.com" data-testid="blacklist-input"
            className="flex-1 bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg placeholder:text-muted/60" />
          <button onClick={addBl} data-testid="blacklist-add-btn"
            className="flex items-center gap-1.5 bg-red-500/90 text-white text-sm font-semibold px-4 py-2 rounded-md hover-lift">
            <Plus size={15} weight="bold" /> Bloquer
          </button>
        </div>
        {bl.length === 0 ? (
          <div className="text-xs text-muted">Aucun compte bloqué.</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="blacklist-list">
            {bl.map((b) => (
              <span key={b.id} className="flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-full border border-red-400/40 bg-red-500/10 text-red-300" data-testid={`blacklist-item-${b.id}`}>
                <span className="opacity-60">{b.type === "domain" ? "@" : ""}</span>{b.value}
                <button onClick={() => removeBl(b.id)} className="text-red-300/70 hover:text-white" data-testid={`blacklist-remove-${b.id}`}><X size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : (
        <div className="surface rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border text-muted">
                  <th className="px-4 py-3 font-medium overline">Utilisateur</th>
                  <th className="px-4 py-3 font-medium overline">Rôle</th>
                  <th className="px-4 py-3 font-medium overline">Abonnement</th>
                  <th className="px-4 py-3 font-medium overline">Actif</th>
                  <th className="px-4 py-3 font-medium overline text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border/60 hover:bg-surface-2/40" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-fg font-medium">
                        {u.role === "admin" && <ShieldCheck size={14} className="text-accent" weight="fill" />}
                        {u.name}
                      </div>
                      <div className="text-xs text-muted font-mono">{u.email}</div>
                      {u.company && <div className="text-xs text-muted">{u.company}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })}>
                        <SelectTrigger className="w-28 h-8 bg-surface-2 border-border text-xs" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.subscription_status || "none"} onValueChange={(v) => patch(u.id, { subscription_status: v })}>
                        <SelectTrigger className="w-32 h-8 bg-surface-2 border-border text-xs" data-testid={`user-sub-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{SUBS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Switch checked={!u.disabled} onCheckedChange={(v) => patch(u.id, { disabled: !v })} data-testid={`user-active-${u.id}`} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="p-2 rounded-md text-muted hover:text-destructive hover:bg-surface-2" data-testid={`user-delete-${u.id}`}>
                            <Trash size={16} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-surface border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer {u.name} ?</AlertDialogTitle>
                            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-surface-2 border-border">Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(u.id)} className="bg-destructive text-white" data-testid={`user-delete-confirm-${u.id}`}>Supprimer</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
